import { ethers } from 'ethers';
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  ReactNode,
  useEffect,
} from 'react';
import {
  WalletState,
  WalletContextType,
  TokenData,
  NetworkInfo,
  SWMBalance,
} from '@/types';
import {
  checkMetaMaskAvailability,
  requestWalletConnection,
  getConnectedAccounts,
  getMetaMaskErrorMessage,
  MetaMaskErrorType,
  addMetaMaskEventListeners,
  removeMetaMaskEventListeners,
} from '../utils/metamask';
import { ensureSWMTokenWatchedOnce } from '../utils/metamask';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { RetryableError } from '@/utils/errorHandling';
import { swmService } from '../services/swmService';
import { networkService } from '../services/networkService';
import { depositService } from '../services/depositService';
import { chatService } from '../services/chatService';
import { contractUpdateService } from '../services/contractUpdateService';
import { generatedApiClient } from '@/services/generatedApiClient';
import { useAuth } from '@/contexts/AuthWrapper';
import { CursorInternal } from 'recharts/types/component/Cursor';
import { Listener } from 'ethers';

const WalletContext = createContext<WalletContextType | undefined>(undefined);

export const useWallet = () => {
  const context = useContext(WalletContext);
  if (context === undefined) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};

interface WalletProviderProps {
  children: ReactNode;
}

const initialWalletState: WalletState = {
  isConnected: false,
  address: null,
  balance: {
    total: '0.00',
    tokens: [],
  },
  connectionStatus: 'idle',
  error: null,
  network: null,
  swmBalance: {
    wallet: '0',
    pool: '0',
    allowance: '0',
  },
  trialBalance: 0,
  developmentMode: process.env.NODE_ENV === 'development',
};

export const WalletProvider: React.FC<WalletProviderProps> = ({ children }) => {
  const [state, setState] = useState<WalletState>(initialWalletState);
  const [isLoading, setIsLoading] = useState(false);
  const [suppressAutoConnect, setSuppressAutoConnect] = useState(false);
  const { handleError, executeWithRetry, retry } = useErrorHandler();
  const [balanceUpdateInterval, setBalanceUpdateInterval] =
    useState<NodeJS.Timeout | null>(null);
  const { user } = useAuth();

  const updateState = useCallback((updates: Partial<WalletState>) => {
    setState(prevState => ({ ...prevState, ...updates }));
  }, []);

  const stateRef = useRef(state);
  const switchingNetworkRef = useRef(false);
  const chainChangeTimerRef = useRef<NodeJS.Timeout | null>(null);
  const trialBalanceFetchRef = useRef<number>(0);

  // Update the ref whenever the state changes
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const getTrialBalanceCacheKey = useCallback((): string => {
    const email = user?.email?.trim().toLowerCase();
    if (email) return `trial-balance:${email}`;
    if (stateRef.current.address) {
      return `trial-balance:${stateRef.current.address}`;
    }
    return 'trial-balance:guest';
  }, [user?.email]);

  useEffect(() => {
    try {
      const cached = localStorage.getItem(getTrialBalanceCacheKey());
      if (cached !== null) {
        const amount = parseFloat(cached);
        if (!Number.isNaN(amount)) {
          updateState({ trialBalance: amount });
          return;
        }
      }
      updateState({ trialBalance: 0 });
    } catch (err) {
      console.warn('Failed to load cached trial balance', err);
    }
  }, [getTrialBalanceCacheKey, updateState]);

  const fetchSWMBalance = useCallback(
    async (address?: string): Promise<void> => {
      const targetAddress = address || state.address;
      if (!targetAddress) return;
      // Only fetch SWM balances when on Swarm devnet; skip on Base to avoid chain switch noise
      if (!stateRef.current.network?.isSwarmDevnet) return;

      try {
        await executeWithRetry(
          async () => {
            const [walletBalance, poolBalance, allowance] = await Promise.all([
              swmService.getBalance(targetAddress),
              depositService.getUserBalance(targetAddress),
              swmService.getAllowance(targetAddress),
            ]);

            updateState({
              swmBalance: {
                wallet: walletBalance,
                pool: poolBalance,
                allowance: allowance,
              },
            });
          },
          {
            maxAttempts: 3,
            baseDelay: 1000,
          }
        );
      } catch (error) {
        console.error('Error fetching SWM balance:', error);
        handleError(error, 'fetchSWMBalance');
      }
    },
    [state.address, updateState, executeWithRetry, handleError]
  );

  const fetchTrialBalance = useCallback(
    async (force: boolean = false): Promise<void> => {
      const now = Date.now();
      if (!force && now - trialBalanceFetchRef.current < 60000) {
        return;
      }
      trialBalanceFetchRef.current = now;

      try {
        const result = await generatedApiClient.getTrialBalance();
        if (!result.error && result.data) {
          const amount = Number(result.data.amount ?? 0);
          const sanitized = Number.isFinite(amount) ? amount : 0;
          updateState({
            trialBalance: sanitized,
          });
          try {
            localStorage.setItem(getTrialBalanceCacheKey(), String(sanitized));
          } catch (cacheErr) {
            console.warn('Failed to cache trial balance', cacheErr);
          }
        }
      } catch (error) {
        console.error('Error fetching trial balance:', error);
      }
    },
    [getTrialBalanceCacheKey, updateState]
  );

  const fetchBalanceForAddress = useCallback(
    async (address: string, isAutoUpdate: boolean = false): Promise<void> => {
      // Only set loading state for manual updates, not auto-updates
      if (!isAutoUpdate) {
        setIsLoading(true);
      }

      try {
        await executeWithRetry(
          async () => {
            // Get SWM token balance
            const swmBalance = await swmService.getBalance(address);
            const swmToken: TokenData = {
              symbol: 'SWM',
              name: 'Swarm Token',
              balance: parseFloat(swmBalance).toLocaleString('en-US', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 9,
              }),
              usdValue: swmBalance, // For now, assume 1:1 with USD
              percentageChange: '0.00',
              isPositiveChange: true,
              icon: '/icons/profile_page/wallet/swm.svg',
              network: 'Swarm',
              networkIcon: '/icons/swarm.svg',
            };

            const allTokens = [swmToken];

            // Calculate total balance
            const totalBalance = allTokens.reduce((sum, token) => {
              const value = parseFloat(token.usdValue.replace(/,/g, ''));
              return sum + value;
            }, 0);

            updateState({
              balance: {
                total: totalBalance.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }),
                tokens: allTokens,
              },
              error: null,
            });

            // Also fetch SWM-specific balances
            await fetchSWMBalance(address);
            // Fetch trial balance periodically alongside wallet updates
            await fetchTrialBalance();
          },
          {
            maxAttempts: 3,
            baseDelay: 1000,
          }
        );
      } catch (error) {
        handleError(error, 'fetchBalanceForAddress');
        updateState({
          error: 'Failed to fetch wallet balance. Please try again.',
        });
      } finally {
        // Only clear loading state for manual updates, not auto-updates
        if (!isAutoUpdate) {
          setIsLoading(false);
        }
      }
    },
    [updateState, executeWithRetry, handleError, fetchSWMBalance, fetchTrialBalance]
  );

  // Start automatic balance updates every 5 seconds
  const startBalanceUpdates = useCallback(() => {
    const currentState = stateRef.current;

    if (balanceUpdateInterval) {
      clearInterval(balanceUpdateInterval);
    }

    const interval = setInterval(async () => {
      if (currentState.isConnected && currentState.address) {
        try {
          await fetchBalanceForAddress(currentState.address, true); // true = isAutoUpdate
        } catch (error) {
          console.warn('Auto balance update failed:', error);
        }
      }
    }, 5000); // 5 seconds

    setBalanceUpdateInterval(interval);
    console.log('🔄 Started automatic balance updates every 5 seconds');
  }, [fetchBalanceForAddress, balanceUpdateInterval]);

  // Stop automatic balance updates
  const stopBalanceUpdates = useCallback(() => {
    if (balanceUpdateInterval) {
      clearInterval(balanceUpdateInterval);
      setBalanceUpdateInterval(null);
      console.log('⏹️ Stopped automatic balance updates');
    }
  }, [balanceUpdateInterval]);

  const detectNetwork = useCallback(async (): Promise<void> => {
    try {
      const networkInfo = await networkService.detectCurrentNetwork();
      updateState({ network: networkInfo });
    } catch (error) {
      console.error('Error detecting network:', error);
      updateState({
        network: {
          chainId: '0x0',
          name: 'Unknown',
          isSupported: false,
          isSwarmDevnet: false,
        },
      });
    }
  }, [updateState]);

  const switchToSwarmNetwork = useCallback(async (): Promise<boolean> => {
    try {
      const result = await networkService.switchToSwarmDevnet();
      if (result.success) {
        await detectNetwork();
        return true;
      } else {
        updateState({ error: result.error || 'Failed to switch network' });
        return false;
      }
    } catch (error) {
      console.error('Error switching network:', error);
      updateState({ error: 'Failed to switch to Swarm network' });
      return false;
    }
  }, [detectNetwork, updateState]);

  const validateNetworkForOperations =
    useCallback(async (): Promise<boolean> => {
      try {
        return await networkService.validateNetworkForDeposit();
      } catch (error) {
        console.error('Error validating network:', error);
        return false;
      }
    }, []);

  const promptNetworkSwitch = useCallback(async (): Promise<void> => {
    if (!state.network || state.network.isSupported) {
      return;
    }

    // In development mode, show different message for local networks
    if (state.developmentMode) {
      updateState({
        error:
          'Please switch to Swarm devnet or a supported local network for deposit operations.',
      });
    } else {
      updateState({
        error: 'Please switch to Swarm devnet to use deposit functionality.',
      });
    }
  }, [state.network, state.developmentMode, updateState]);

  // Validate all contracts on connection
  const validateContracts = useCallback(async (): Promise<boolean> => {
    try {
      const validationResults =
        await contractUpdateService.validateAllContracts();
      const failedValidations = validationResults.filter(
        result => !result.isValid
      );

      if (failedValidations.length > 0) {
        const failedContracts = failedValidations
          .map(result => result.contractName)
          .join(', ');
        updateState({
          error: `Contract validation failed for: ${failedContracts}. Some features may not work correctly.`,
        });
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error validating contracts:', error);
      updateState({
        error:
          'Unable to validate contract deployments. Some features may not work correctly.',
      });
      return false;
    }
  }, [updateState]);

  // Connection success handler that stores wallet address and triggers balance fetch
  const handleConnectionSuccess = useCallback(
    async (address: string): Promise<void> => {
      updateState({
        isConnected: true,
        address,
        connectionStatus: 'connected',
        error: null,
      });

      // Detect network after connection
      await detectNetwork();

      // Validate network and auto-switch if needed
      let isNetworkValid = await validateNetworkForOperations();
      if (!isNetworkValid) {
        const switched = await switchToSwarmNetwork();
        if (!switched) {
          await promptNetworkSwitch();
          return; // Don't proceed with balance fetch if network is invalid
        }
        // Re-detect and re-validate after successful switch
        await detectNetwork();
        isNetworkValid = await validateNetworkForOperations();
        if (!isNetworkValid) {
          await promptNetworkSwitch();
          return;
        }
      }

      // Validate contract deployments
      const contractsValid = await validateContracts();
      if (!contractsValid) {
        console.warn(
          'Some contracts failed validation, but proceeding with connection'
        );
      }

      // Attempt to add SWM token to MetaMask once per chain
      await ensureSWMTokenWatchedOnce();

      // Send address to backend for chat service (skip in development to avoid CORS noise)
      try {
 
        await chatService.setWalletAddress(address);
      } catch (error) {
        // Don't fail the connection if address setting fails
      }

      // Fetch balance after successful connection
      await fetchBalanceForAddress(address);
      await fetchTrialBalance(true);

      // Start automatic balance updates
      startBalanceUpdates();
    },
    [
      updateState,
      fetchBalanceForAddress,
      fetchTrialBalance,
      detectNetwork,
      validateNetworkForOperations,
      promptNetworkSwitch,
      validateContracts,
      startBalanceUpdates,
    ]
  );

  // Connection error handler with user-friendly error messages and retry options
  const handleConnectionError = useCallback(
    (error: unknown): void => {
      const userFriendlyMessage = getMetaMaskErrorMessage(error);

      console.error('Wallet connection error:', error);

      updateState({
        connectionStatus: 'error',
        error: userFriendlyMessage,
      });
    },
    [updateState]
  );

  // Handle MetaMask account changes
  const handleAccountsChanged = useCallback(
    (accounts: string[]) => {
      if (accounts.length === 0) {
        // User disconnected all accounts
        setState(initialWalletState);
      } else if (accounts[0] !== state.address) {
        // User switched to a different account
        handleConnectionSuccess(accounts[0]);
      }
    },
    [state.address, handleConnectionSuccess]
  );

  // Handle MetaMask chain changes
  const handleChainChanged = useCallback(
    async (_chainId: string) => {
      // Debounce rapid chainChanged events
      if (chainChangeTimerRef.current) {
        clearTimeout(chainChangeTimerRef.current);
      }
      chainChangeTimerRef.current = setTimeout(async () => {
        if (switchingNetworkRef.current) return;
        switchingNetworkRef.current = true;
        stopBalanceUpdates();
        await detectNetwork();
        if (stateRef.current.isConnected) {
          const isNetworkValid = await validateNetworkForOperations();
          if (!isNetworkValid) {
            const switched = await switchToSwarmNetwork();
            if (!switched) {
              await promptNetworkSwitch();
            } else {
              await detectNetwork();
              await ensureSWMTokenWatchedOnce();
            }
          }
        }
        if (stateRef.current.isConnected && stateRef.current.address) {
          await fetchBalanceForAddress(stateRef.current.address, true);
        }
        switchingNetworkRef.current = false;
        startBalanceUpdates();
      }, 600);
    },
    [
      fetchBalanceForAddress,
      detectNetwork,
      validateNetworkForOperations,
      promptNetworkSwitch,
      stopBalanceUpdates,
      startBalanceUpdates,
      switchToSwarmNetwork,
      ensureSWMTokenWatchedOnce,
    ]
  );

  // Handle contract address updates
  const handleContractUpdate = useCallback(
    async (updateEvent: any) => {
      console.log('Contract update detected:', updateEvent);

      try {
        // Validate the new contract deployment if it's an address update
        if (updateEvent.type === 'address') {
          const validationResult =
            await contractUpdateService.validateContractDeployment(
              updateEvent.contractName,
              updateEvent.newValue
            );

          if (!validationResult.isValid) {
            console.error(
              'Contract validation failed:',
              validationResult.error
            );
            updateState({
              error: `Contract update validation failed for ${updateEvent.contractName}: ${validationResult.error}`,
            });
            return;
          }
        }

        // Refresh balances when contract addresses change
        if (state.isConnected && state.address) {
          await fetchBalanceForAddress(state.address, true); // true = isAutoUpdate
          console.log('Balances refreshed after contract update');
        }
      } catch (error) {
        console.error('Error handling contract update:', error);
        updateState({
          error:
            'Contract update detected but validation failed. Please refresh manually.',
        });
      }
    },
    [state.isConnected, state.address, fetchBalanceForAddress, updateState]
  );

  // Handle contract deployment validation failures
  const handleContractValidationError = useCallback(
    (error: any) => {
      console.error('Contract validation error:', error);
      updateState({
        error:
          'Contract deployment validation failed. Some features may not work correctly.',
      });
    },
    [updateState]
  );

  // Set up MetaMask event listeners and contract update listeners
  useEffect(() => {
    const initializeWallet = async () => {
      if (checkMetaMaskAvailability()) {
        addMetaMaskEventListeners(handleAccountsChanged, handleChainChanged);

        // Check if already connected on mount - with error handling
        try {
          const accounts = await getConnectedAccounts();
          if (
            accounts.length > 0 &&
            !state.isConnected &&
            !suppressAutoConnect
          ) {
            await handleConnectionSuccess(accounts[0]);
          } else {
            // Even if not connected, detect network
            // await detectNetwork();
          }
        } catch (error) {
          console.warn(
            'Auto-connect check failed, continuing without connection:',
            error
          );
          // Don't fail the entire initialization, just continue without auto-connect
          // await detectNetwork();
        }
      }
    };

    // Call the async function
    initializeWallet();

    // Set up contract update listeners
    contractUpdateService.on('update', handleContractUpdate);
    contractUpdateService.on('addressUpdate', handleContractUpdate);
    contractUpdateService.on('error', handleContractValidationError);

    // Start watching for contract updates if not already watching
    if (!contractUpdateService.isCurrentlyWatching()) {
      contractUpdateService.startWatching();
    }

    return () => {
      if (checkMetaMaskAvailability()) {
        removeMetaMaskEventListeners(handleAccountsChanged, handleChainChanged);
      }

      // Remove contract update listeners
      contractUpdateService.off('update', handleContractUpdate);
      contractUpdateService.off('addressUpdate', handleContractUpdate);
      contractUpdateService.off('error', handleContractValidationError);
    };
  }, [
    handleAccountsChanged,
    handleChainChanged,
    state.isConnected,
    handleConnectionSuccess,
    detectNetwork,
    handleContractUpdate,
    handleContractValidationError,
    suppressAutoConnect,
  ]);

  // Wallet connection request handler with error handling for rejection scenarios
  const connect = useCallback(async (): Promise<void> => {
    // MetaMask detection utility function to check if extension is installed
    if (!checkMetaMaskAvailability()) {
      handleConnectionError({
        type: MetaMaskErrorType.NOT_INSTALLED,
        message: 'MetaMask is not installed',
      });
      return;
    }

    setIsLoading(true);
    updateState({ connectionStatus: 'connecting', error: null });

    try {
      setSuppressAutoConnect(false);
      await executeWithRetry(
        async () => {
          // Request wallet connection using utility function
          const accounts = await requestWalletConnection();
          const address = accounts[0];

          // Handle connection success
          await handleConnectionSuccess(address);
        },
        {
          maxAttempts: 2, // Don't retry too many times for user rejection
          baseDelay: 500,
        }
      );
    } catch (error) {
      // Handle connection error with user-friendly messages
      handleConnectionError(error);
      handleError(error, 'walletConnect');
    } finally {
      setIsLoading(false);
    }
  }, [
    updateState,
    handleConnectionSuccess,
    handleConnectionError,
    executeWithRetry,
    handleError,
  ]);

  const disconnect = useCallback(async (): Promise<void> => {
    setIsLoading(true);

    try {
      // Remove MetaMask event listeners
      removeMetaMaskEventListeners(handleAccountsChanged, handleChainChanged);

      // Attempt to revoke site permissions so MetaMask forgets connected accounts
      try {
        if ((window as any).ethereum?.request) {
          await (window as any).ethereum.request({
            method: 'wallet_revokePermissions',
            params: [{ eth_accounts: {} }],
          });
        }
      } catch (permErr) {
        console.warn(
          'Could not revoke MetaMask permissions (non-fatal):',
          permErr
        );
      }

      // Send empty address to backend to clear user session
      try {
        const { chatService } = await import('@/services/chatService');
        await chatService.deleteWalletAddress();
        console.log('🔗 Cleared wallet address on backend');
      } catch (backendErr) {
        console.warn(
          'Failed to clear wallet address on backend (non-fatal):',
          backendErr
        );
      }

      // Stop automatic balance updates
      stopBalanceUpdates();

      // Reset wallet state
      setState(initialWalletState);
      setSuppressAutoConnect(true);

      console.log('🔌 Wallet disconnected successfully');
    } catch (error) {
      console.error('Wallet disconnect error:', error);
      updateState({
        error: 'Failed to disconnect wallet properly.',
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    updateState,
    handleAccountsChanged,
    handleChainChanged,
    stopBalanceUpdates,
  ]);

  const fetchBalance = useCallback(async (): Promise<void> => {
    if (!state.isConnected || !state.address) {
      return;
    }

    await fetchBalanceForAddress(state.address);
  }, [state.isConnected, state.address, fetchBalanceForAddress]);

  const fetchSWMBalanceWrapper = useCallback(async (): Promise<void> => {
    if (!state.isConnected || !state.address) {
      return;
    }

    await fetchSWMBalance(state.address);
  }, [state.isConnected, state.address, fetchSWMBalance]);

  const fetchTrialBalanceWrapper = useCallback(async (): Promise<void> => {
    await fetchTrialBalance(true);
  }, [fetchTrialBalance]);

  const retryConnection = useCallback(async (): Promise<void> => {
    await retry(connect);
  }, [retry, connect]);

  const retryBalanceFetch = useCallback(async (): Promise<void> => {
    if (state.address) {
      await retry(() => fetchBalanceForAddress(state.address!));
    }
  }, [retry, fetchBalanceForAddress, state.address]);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (balanceUpdateInterval) {
        clearInterval(balanceUpdateInterval);
      }
    };
  }, [balanceUpdateInterval]);

  const value: WalletContextType = {
    state,
    connect,
    disconnect,
    fetchBalance,
    fetchSWMBalance: fetchSWMBalanceWrapper,
    fetchTrialBalance: fetchTrialBalanceWrapper,
    switchToSwarmNetwork,
    isLoading,
    retryConnection,
    retryBalanceFetch,
  };

  return (
    <WalletContext.Provider value={value}>{children}</WalletContext.Provider>
  );
};
